import { useState } from "react";
import { PrimaryButton, SecondaryButton } from "@/components/StandardButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2 } from "lucide-react";
import { isMobileDevice } from "@/utils/mobileOptimizations";
import { DisplayM, BodyText } from "@/components/ui/typography";

interface WelcomeStepProps {
  onSubmit: (companyName: string) => void;
  loading: boolean;
  error: string;
}

export const WelcomeStep = ({ onSubmit, loading, error }: WelcomeStepProps) => {
  const [companyName, setCompanyName] = useState("");
  const [localError, setLocalError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (!companyName.trim()) {
      setLocalError("Company name is required");
      return;
    }

    if (companyName.trim().length > 100) {
      setLocalError("Company name must be less than 100 characters");
      return;
    }

    setLocalError("");
    onSubmit(companyName);
  };

  const displayError = error || localError;

  return (
    <div className="p-2 sm:p-4">
      {/* Editorial header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="h-px w-10 bg-foreground/20" />
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold font-editorial text-muted-foreground">
            № 001 · Setup
          </span>
          <span className="h-px w-10 bg-foreground/20" />
        </div>
        <h1 className="font-serif italic text-3xl sm:text-4xl font-normal leading-[1.05] text-foreground mb-3">
          Welcome to Sendfeedback.
        </h1>
        <BodyText className="text-muted-foreground text-sm">
          Let's set up your workspace in under 2 minutes.
        </BodyText>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="company-name" className="text-sm">
            What's your company name? *
          </Label>
          <Input
            id="company-name"
            type="text"
            placeholder="Acme Inc."
            value={companyName}
            onChange={(e) => {
              setCompanyName(e.target.value);
              setLocalError("");
            }}
            disabled={loading}
            required
            maxLength={100}
            autoFocus={!isMobileDevice()}
            className="min-h-[44px] text-base"
          />
          {displayError && (
            <p className="text-sm text-destructive">{displayError}</p>
          )}
        </div>

        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-sm font-medium text-foreground mb-1.5">What happens next:</p>
          <ul className="text-sm text-muted-foreground space-y-0.5">
            <li>✓ Create your feedback page</li>
            <li>✓ Customize your branding</li>
            <li>✓ Get sharing options (QR, link, email)</li>
            <li>✓ Set up automated campaigns</li>
          </ul>
        </div>

        <PrimaryButton type="submit" className="w-full min-h-[44px] text-sm" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Creating your workspace...
            </>
          ) : (
            "Get Started →"
          )}
        </PrimaryButton>
      </form>
    </div>
  );
};
